import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  FaHome,
  FaBuilding,
  FaUsers,
  FaShareAlt,
  FaAddressBook,
  FaFolderOpen,
  FaBriefcase,
  FaChartLine,
  FaUserFriends,
  FaHeadset,
  FaSignOutAlt,
  FaBars,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaCog,
  FaBullhorn,
  FaGlobe,
  FaExchangeAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import "../assets/css/CustomerPortal.css";
import axiosInstance from "../components/api/axiosInstance";
import TenantDashboard from "./TenantDashboard";
import TenantLeads from "./TenantLeads";
import RegisterLead from "./RegisterLead";
import TenantLeadView from "./TenantLeadView";
import TenantWebsiteLeads from "./TenantWebsiteLeads";
import TenantProfileView from "./TenantProfileView";
import TenantProfileEdit from "./TenantProfileEdit";
import TenantProfileLayout from "./TenantProfileLayout";
import TenantChangePassword from "./TenantChangePassword";
import TenantCompanyProfile from "./TenantCompanyProfile";
import TenantEmployees from "./TenantEmployees";
import TenantEmployeeView from "./TenantEmployeeView";
import TenantSocialAccounts from "./TenantSocialAccounts";
import TenantSocialAccountView from "./TenantSocialAccountView";
import TenantCustomers from "./TenantCustomers";
import TenantCustomerView from "./TenantCustomerView";
import TenantClients from "./TenantClients";
import TenantClientView from "./TenantClientView";
import TenantProjects from "./TenantProjects";
import TenantServices from "./TenantServices";
import TenantSupport from "./TenantSupport";
import TenantSettings from "./TenantSettings";
import DMProjects from "../pages/dm/DMProjects";
import DMProjectWorkspace from "../pages/dm/DMProjectWorkspace";
import { portalDmApi } from "../pages/dm/dmApi";
import TenantDmProjectNew from "./TenantDmProjectNew";
import PortalNoticeBanner from "../components/PortalNoticeBanner";
import { currentPortalHost, resolvePortalHost } from "../context/PortalHostContext";
import { clearAuthSessionStorage } from "../utils/clearSessionStorage";
import {
  getSessionTenants,
  persistSignedInSession,
  setAuthItem,
  updateClientPortalProfile,
} from "../utils/authStorage";
import { getPortalModules, getPortalSession, isModuleEnabled, isPortalOwner } from "./portalSession";
import { getCtxHomeUrl } from "../config/ctxPath";
import {
  getSidebarTheme,
  normalizeSidebarTheme,
  setSidebarTheme,
  sidebarThemeClassName,
} from "../utils/sidebarTheme";

const NAV_ITEMS = [
  { to: "/portal", end: true, label: "Dashboard", icon: FaHome },
  { to: "/portal/company-profile", label: "Company Profile", icon: FaBuilding, ownerOnly: true },
  { to: "/portal/employees", label: "Employees", icon: FaUsers, ownerOnly: true },
  { to: "/portal/clients", label: "Clients", icon: FaAddressBook, module: "clients" },
  { to: "/portal/projects", label: "Projects", icon: FaFolderOpen, module: "projects" },
  { to: "/portal/services", label: "Services", icon: FaBriefcase, module: "services" },
  { to: "/portal/social-accounts", label: "Social Accounts", icon: FaShareAlt, module: "social_accounts" },
  { to: "/portal/digital-marketing", label: "Digital Marketing", icon: FaBullhorn, module: "digital_marketing" },
  { to: "/portal/leads", label: "Leads", icon: FaChartLine },
  { to: "/portal/website-leads", label: "Website Leads", icon: FaGlobe },
  { to: "/portal/customers", label: "Customers", icon: FaUserFriends, module: "customers" },
];

function getCustomer() {
  return getPortalSession();
}

function companyInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "C";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
}

function formatTitleCase(value) {
  return String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function TenantPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const customer = useMemo(getCustomer, []);
  const portalOwner = isPortalOwner(customer);
  const [modules, setModules] = useState(() => getPortalModules(customer));
  const navItems = useMemo(
    () =>
      NAV_ITEMS.filter(
        (item) => (portalOwner || !item.ownerOnly) && isModuleEnabled(modules, item.module)
      ),
    [portalOwner, modules]
  );
  const moduleRoute = (module, element) => {
    if (modules === null) return null;
    return isModuleEnabled(modules, module) ? element : <Navigate to="/portal" replace />;
  };
  const [companyName, setCompanyName] = useState(customer.Company_Name || "");
  const [businesses, setBusinesses] = useState(getSessionTenants);
  const [switching, setSwitching] = useState(false);
  const [businessMenuOpen, setBusinessMenuOpen] = useState(false);
  const currentTenantId = customer.Tenant_Id ?? customer.client_id;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("cportal_sidebar_collapsed") === "1";
    } catch (_) {
      return false;
    }
  });
  const [sidebarTheme, setSidebarThemeState] = useState(
    () => normalizeSidebarTheme(customer.Sidebar_Theme, "portal") || getSidebarTheme("portal")
  );

  useEffect(() => {
    const fromAccount = normalizeSidebarTheme(customer.Sidebar_Theme, "portal");
    if (fromAccount && fromAccount !== getSidebarTheme("portal")) {
      setSidebarTheme("portal", fromAccount);
    }
  }, [customer]);

  useEffect(() => {
    const sync = (e) => {
      if (e?.detail?.scope === "portal" && e.detail.theme) {
        setSidebarThemeState(e.detail.theme);
        return;
      }
      setSidebarThemeState(getSidebarTheme("portal"));
    };
    window.addEventListener("sidebarThemeChanged", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("sidebarThemeChanged", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/client/company");
        const company = res.data?.data || {};
        if (!active) return;
        if (company.Company_Name) setCompanyName(String(company.Company_Name).trim());
        if (Array.isArray(company.Enabled_Modules)) {
          setModules(company.Enabled_Modules);
          updateClientPortalProfile({ ...getPortalSession(), Enabled_Modules: company.Enabled_Modules });
        } else {
          setModules((prev) => prev ?? []);
        }
      } catch (_) {
        // Keep company name from login session; without a module list only core items stay visible.
        if (active) setModules((prev) => prev ?? []);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/my-tenants");
        const list = res.data?.data?.tenants;
        if (active && Array.isArray(list)) {
          setBusinesses(list);
          setAuthItem("tenants", JSON.stringify(list));
        }
      } catch (_) {
        // Keep the list from the sign-in response.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  /** A company on the same person's other login (own domain): open it signed in, no password. */
  const openLinkedLoginCompany = async (choice) => {
    const signInThere = () => {
      if (!choice.Portal_URL) {
        window.alert(`Sign in to ${choice.Company_Name || "this company"} with ${choice.Login_Email}.`);
        return;
      }
      const url = new URL(choice.Portal_URL, window.location.origin);
      if (choice.Login_Email) url.searchParams.set("email", choice.Login_Email);
      window.location.href = url.toString();
    };
    try {
      const res = await axiosInstance.post("/linked-login-handoff", { tenant_id: choice.Tenant_Id });
      const handoff = res.data?.data;
      if (res.data?.status !== "success" || !handoff?.token) {
        signInThere();
        return;
      }
      const target = await resolvePortalHost();
      const otherHost =
        target?.enforced &&
        target.kind !== "dev" &&
        handoff.portal_url &&
        handoff.portal_host !== currentPortalHost();
      if (otherHost) {
        const url = new URL(handoff.portal_url);
        url.searchParams.set("handoff", handoff.token);
        window.location.href = url.toString();
        return;
      }
      const exchange = await axiosInstance.post("/linked-login-handoff/exchange", {
        token: handoff.token,
        portal_host: currentPortalHost(),
      });
      if (exchange.data?.status === "success" && exchange.data.data) {
        persistSignedInSession(exchange.data.data);
        window.location.href = getCtxHomeUrl();
        return;
      }
      signInThere();
    } catch (_) {
      signInThere();
    }
  };

  const handleSwitchBusiness = async (value) => {
    const tenantId = Number(value);
    if (!tenantId || tenantId === Number(currentTenantId)) return;
    const choice = businesses.find((b) => Number(b.Tenant_Id) === tenantId);
    if (choice?.Requires_Sign_In) {
      setSwitching(true);
      await openLinkedLoginCompany(choice);
      setSwitching(false);
      return;
    }
    setSwitching(true);
    try {
      const res = await axiosInstance.post("/switch-tenant", { tenant_id: tenantId });
      if (res.data?.status === "success" && res.data.data) {
        const portal = res.data.data.portal;
        const target = await resolvePortalHost();
        // Once hosts are enforced, a company on another portal host is opened there (sign in again).
        if (portal?.host_mismatch && portal.url && target?.enforced && target.kind !== "dev") {
          window.location.href = portal.url;
          return;
        }
        persistSignedInSession(res.data.data);
        window.location.href = getCtxHomeUrl();
        return;
      }
      window.alert(res.data?.message || "Could not switch company.");
    } catch (err) {
      window.alert(err.response?.data?.message || "Could not switch company.");
    }
    setSwitching(false);
  };

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [sidebarOpen]);

  const brandName = formatTitleCase(companyName.trim() || "Cloud Connect");
  const brandInitial = companyInitials(brandName);
  const displayName =
    customer.Full_Name ||
    `${customer.First_Name || ""} ${customer.Last_Name || ""}`.trim() ||
    "Client";
  const userEmail =
    customer.Company_Email ||
    customer.Email ||
    customer.Company_Email ||
    customer.company_email ||
    customer.email ||
    "";
  const initial = (displayName || "C").charAt(0).toUpperCase();
  const profileActive = location.pathname.startsWith("/portal/profile");

  const handleLogout = () => {
    clearAuthSessionStorage();
    window.location.href = getCtxHomeUrl();
  };

  const handleProfileClick = () => {
    setSidebarOpen(false);
    navigate("/portal/profile");
  };

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("cportal_sidebar_collapsed", next ? "1" : "0");
      } catch (_) {}
      return next;
    });
  };

  return (
    <div
      className={`cportal${sidebarOpen ? " cportal-sidebar-open" : ""}${
        sidebarCollapsed ? " cportal-sidebar-collapsed" : ""
      } ${sidebarThemeClassName(sidebarTheme)}`}
    >
      {sidebarOpen && (
        <button
          type="button"
          className="cportal-sidebar-backdrop"
          aria-label="Close navigation menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className="cportal-sidebar">
        <div className="cportal-sidebar-brand">
          <span className="cportal-brand-mark" title={brandName}>
            {brandInitial}
          </span>
          <span className="cportal-brand-text" title={brandName}>
            {brandName}
          </span>
        </div>

        <nav className="cportal-nav">
          {navItems.map(({ to, end, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className="cportal-nav-link"
              title={label}
              onClick={() => setSidebarOpen(false)}
            >
              <Icon className="cportal-nav-link-icon" aria-hidden="true" />
              <span className="cportal-nav-link-label">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="cportal-sidebar-footer">
          {businesses.length > 1 && (
            <>
              <div
                className="cportal-sidebar-link-divider cportal-business-switch-divider"
                aria-hidden="true"
              />
              <div className="cportal-business-switch">
                <button
                  type="button"
                  className="cportal-nav-link cportal-footer-link cportal-business-toggle"
                  aria-expanded={businessMenuOpen}
                  aria-controls="cportal-business-list"
                  title="Switch Business"
                  onClick={() => setBusinessMenuOpen((open) => !open)}
                  disabled={switching}
                >
                  <FaExchangeAlt className="cportal-nav-link-icon" aria-hidden="true" />
                  <span className="cportal-nav-link-label">Switch Business</span>
                  {businessMenuOpen ? (
                    <FaChevronUp className="cportal-business-toggle-chevron" aria-hidden="true" />
                  ) : (
                    <FaChevronDown className="cportal-business-toggle-chevron" aria-hidden="true" />
                  )}
                </button>
                {businessMenuOpen && (
                  <ul id="cportal-business-list" className="cportal-business-list">
                    {businesses
                      .filter((b) => Number(b.Tenant_Id) !== Number(currentTenantId))
                      .map((b) => {
                        const note = b.Is_Platform
                          ? "Tumma staff"
                          : b.Requires_Sign_In
                            ? b.Login_Email || "Own login"
                            : b.scope === "owner"
                              ? "Owner"
                              : "";
                        return (
                          <li key={b.Tenant_Id}>
                            <button
                              type="button"
                              className="cportal-business-option"
                              title={b.Company_Name || `Company #${b.Tenant_Id}`}
                              onClick={() => handleSwitchBusiness(b.Tenant_Id)}
                              disabled={switching}
                            >
                              <span className="cportal-business-option-name">
                                {b.Company_Name || `Company #${b.Tenant_Id}`}
                              </span>
                              {note && <span className="cportal-business-option-note">{note}</span>}
                            </button>
                          </li>
                        );
                      })}
                  </ul>
                )}
              </div>
            </>
          )}

          <div className="cportal-sidebar-footer-divider">
            <button
              type="button"
              className="cportal-sidebar-collapse-btn"
              onClick={toggleSidebarCollapse}
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCollapsed ? (
                <FaAngleDoubleRight size={13} aria-hidden="true" />
              ) : (
                <FaAngleDoubleLeft size={13} aria-hidden="true" />
              )}
            </button>
          </div>

          <div className="cportal-sidebar-footer-links" role="navigation" aria-label="Account actions">
            <NavLink
              to="/portal/support"
              className="cportal-nav-link cportal-footer-link"
              title="Support"
              onClick={() => setSidebarOpen(false)}
            >
              <FaHeadset className="cportal-nav-link-icon" aria-hidden="true" />
              <span className="cportal-nav-link-label">Support</span>
            </NavLink>
            <NavLink
              to="/portal/settings"
              className="cportal-nav-link cportal-footer-link"
              title="Settings"
              onClick={() => setSidebarOpen(false)}
            >
              <FaCog className="cportal-nav-link-icon" aria-hidden="true" />
              <span className="cportal-nav-link-label">Settings</span>
            </NavLink>
            <button
              type="button"
              className="cportal-nav-link cportal-footer-link cportal-footer-link--signout"
              title="Sign out"
              onClick={handleLogout}
            >
              <FaSignOutAlt className="cportal-nav-link-icon" aria-hidden="true" />
              <span className="cportal-nav-link-label">Sign out</span>
            </button>
          </div>

          <div className="cportal-sidebar-link-divider" aria-hidden="true" />

          <button
            type="button"
            className={`cportal-account-trigger${profileActive ? " is-active" : ""}`}
            onClick={handleProfileClick}
            title={displayName}
          >
            <span className="cportal-account-trigger-avatar" aria-hidden="true">
              {initial}
            </span>
            <span className="cportal-account-trigger-details">
              <span className="cportal-account-trigger-name">{displayName}</span>
              <span className="cportal-account-trigger-email">{userEmail || "—"}</span>
            </span>
          </button>
        </div>
      </aside>

      <div className="cportal-main">
        <header className="cportal-mobile-bar">
          <span className="cportal-mobile-bar-title" title={brandName}>
            {brandName}
          </span>
          <button
            type="button"
            className="cportal-sidebar-toggle"
            onClick={() => setSidebarOpen((open) => !open)}
            aria-label={sidebarOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={sidebarOpen}
          >
            <FaBars aria-hidden="true" />
          </button>
        </header>

        <main className="cportal-content">
          <PortalNoticeBanner />
          <Routes>
            <Route path="/portal" element={<TenantDashboard modules={modules} />} />
            <Route path="/portal/clients" element={moduleRoute("clients", <TenantClients />)} />
            <Route
              path="/portal/clients/new"
              element={moduleRoute("clients", <TenantClients createMode />)}
            />
            <Route
              path="/portal/clients/:id"
              element={moduleRoute("clients", <TenantClientView modules={modules} />)}
            />
            <Route path="/portal/projects" element={moduleRoute("projects", <TenantProjects />)} />
            <Route
              path="/portal/projects/new"
              element={moduleRoute("projects", <TenantProjects createMode />)}
            />
            <Route path="/portal/services" element={moduleRoute("services", <TenantServices />)} />
            <Route
              path="/portal/digital-marketing"
              element={moduleRoute(
                "digital_marketing",
                <DMProjects api={portalDmApi} basePath="/portal/digital-marketing" />
              )}
            />
            <Route
              path="/portal/digital-marketing/new"
              element={moduleRoute(
                "digital_marketing",
                <TenantDmProjectNew api={portalDmApi} basePath="/portal/digital-marketing" />
              )}
            />
            <Route
              path="/portal/digital-marketing/:id"
              element={moduleRoute(
                "digital_marketing",
                <DMProjectWorkspace api={portalDmApi} basePath="/portal/digital-marketing" />
              )}
            />
            <Route path="/portal/leads" element={<TenantLeads mode="leads" />} />
            <Route path="/portal/website-leads" element={<TenantWebsiteLeads />} />
            <Route path="/portal/customers" element={moduleRoute("customers", <TenantCustomers />)} />
            <Route
              path="/portal/customers/new"
              element={moduleRoute("customers", <TenantCustomers createMode />)}
            />
            <Route
              path="/portal/customers/:id"
              element={moduleRoute("customers", <TenantCustomerView />)}
            />
            <Route path="/portal/leads/new" element={<RegisterLead />} />
            <Route path="/portal/leads/:id" element={<TenantLeadView />} />
            <Route path="/portal/company-profile" element={portalOwner ? <TenantCompanyProfile /> : <Navigate to="/portal" replace />} />
            <Route path="/portal/employees" element={portalOwner ? <TenantEmployees /> : <Navigate to="/portal" replace />} />
            <Route path="/portal/employees/new" element={portalOwner ? <TenantEmployees createMode /> : <Navigate to="/portal" replace />} />
            <Route path="/portal/employees/:id" element={portalOwner ? <TenantEmployeeView /> : <Navigate to="/portal" replace />} />
            <Route
              path="/portal/social-accounts"
              element={moduleRoute("social_accounts", <TenantSocialAccounts />)}
            />
            <Route
              path="/portal/social-accounts/new"
              element={moduleRoute("social_accounts", <TenantSocialAccounts createMode />)}
            />
            <Route
              path="/portal/social-accounts/:id"
              element={moduleRoute("social_accounts", <TenantSocialAccountView />)}
            />
            <Route path="/portal/support" element={<TenantSupport />} />
            <Route path="/portal/settings" element={<TenantSettings />} />
            <Route path="/portal/profile" element={<TenantProfileLayout />}>
              <Route index element={<TenantProfileView />} />
              <Route path="edit" element={<TenantProfileEdit />} />
              <Route path="change-password" element={<TenantChangePassword />} />
            </Route>
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default TenantPortal;
